/**
 * Срок и отмена запроса: свой `AbortController`, который срабатывает по
 * таймеру (`TIMEOUT`) или вслед за сигналом вызывающего (`ABORTED`).
 * `stopped` отклоняется в этот момент — им запрос обрывается на любом шаге,
 * даже пока ждёт токен, который `fetch` отменить не может. Причина отмены —
 * ошибка от `reason`, её же видит `signal.reason`.
 *
 * Отдельно от клиента API: без i18n и сторов, чтобы проверяться в Node.
 */

export type StopCode = 'TIMEOUT' | 'ABORTED';

export interface Deadline {
  readonly signal: AbortSignal;
  readonly stopped: Promise<never>;
  /** Запрос закончился: снять таймер и подписку на сигнал вызывающего. */
  readonly dispose: () => void;
}

export function deadline(signal: AbortSignal | null | undefined, timeoutMs: number | undefined, reason: (code: StopCode) => Error): Deadline {
  const controller = new AbortController();
  const stopped = new Promise<never>((_resolve, reject) => {
    controller.signal.addEventListener('abort', () => reject(controller.signal.reason), { once: true });
  });
  // Запрос закончился раньше — отказ `stopped` никто не ждёт, и это не ошибка.
  stopped.catch(() => undefined);
  const onAbort = () => controller.abort(reason('ABORTED'));
  if (signal?.aborted === true) onAbort();
  else signal?.addEventListener('abort', onAbort, { once: true });
  const timer = timeoutMs === undefined ? undefined : setTimeout(() => controller.abort(reason('TIMEOUT')), timeoutMs);
  return {
    signal: controller.signal,
    stopped,
    dispose: () => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
    },
  };
}
